import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Quote } from 'lucide-react';
import { API } from '../utils/authAxios';

/**
 * Carrusel/grid de testimonios públicos del Reto 21 Días.
 * - Auto-fetch desde /api/public/reto21/testimonios
 * - Si no hay testimonios aprobados, no renderiza nada (degrada elegantemente).
 *
 * Props:
 *  - title: encabezado
 *  - subtitle: bajada
 *  - limit: cuántos traer (default 6)
 *  - featuredOnly: solo destacados
 *  - layout: 'grid' (default) | 'rotator' (uno solo, alterna cada 8s)
 */
export default function Reto21TestimoniosCarousel({
  title = 'Lo que dicen quienes terminaron el Reto',
  subtitle = '',
  limit = 6,
  featuredOnly = false,
  layout = 'grid',
}) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios
      .get(API + '/public/reto21/testimonios', { params: { limit, featured_only: featuredOnly } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, [limit, featuredOnly]);

  useEffect(() => {
    if (layout !== 'rotator' || items.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 8000);
    return () => clearInterval(id);
  }, [layout, items.length]);

  if (!loaded || items.length === 0) return null;

  const meta = (t) => {
    const parts = [];
    if (t.edad) parts.push(`${t.edad} años`);
    if (t.region) parts.push(t.region);
    return parts.join(' · ');
  };

  const Card = ({ t }) => (
    <div
      className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 hover:border-emerald-700/40 transition"
      data-testid={`testimonio-card-${t.id}`}
    >
      <Quote className="w-6 h-6 text-emerald-500/40 mb-2" />
      <p className="text-slate-200 text-sm leading-relaxed italic mb-4 line-clamp-6">
        {t.texto}
      </p>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white text-sm font-semibold">{t.nombre_display || 'Anónimo'}</p>
          {meta(t) && <p className="text-slate-400 text-xs">{meta(t)}</p>}
        </div>
        {t.rating && (
          <div className="flex gap-0.5">
            {Array.from({ length: t.rating }).map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (layout === 'rotator') {
    const t = items[idx];
    return (
      <section className="py-8" data-testid="testimonio-rotator">
        <div className="max-w-2xl mx-auto">
          {title && <h3 className="text-sm uppercase tracking-wider text-emerald-400 mb-3 text-center">{title}</h3>}
          <Card t={t} />
          {items.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-4">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-emerald-500' : 'w-1.5 bg-slate-700 hover:bg-slate-600'}`}
                  aria-label={`Ir al testimonio ${i + 1}`}
                  data-testid={`rotator-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="py-12" data-testid="testimonio-grid">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">{title}</h2>
          {subtitle && <p className="text-slate-400 mt-2">{subtitle}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => <Card key={t.id} t={t} />)}
        </div>
      </div>
    </section>
  );
}
